import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../Icon';
import s from '../../pages/Home.module.css';
import e from './ExpandCard.module.css';

/**
 * A dashboard card that opens up (Lj, Oct 16: "line chart cards I can click on and they expand … expand gives me more
 * details"). The Earned per hour card's anatomy: title + sub, two headline figures with captions, the chart, a ranked
 * list, then ✓ what counted · ⊖ what was left out. Collapsed it is one button; expanded it spans the grid row, the
 * chart grows, the list shows everything, and `detail` (a table, a range switch, the goal) appears. Esc or the ✕
 * closes it. Transform/opacity only, 200 ms, none under prefers-reduced-motion.
 *
 *   figures  [{ value, caption, tone?: 'met' | 'close' | 'over' | null }]
 *   chart    (expanded: boolean) → node
 *   list     (expanded: boolean) → node     the ranked rows (show ~5 collapsed, all expanded)
 *   detail   node                             only when expanded (table, range, goal …)
 *   counted  string · leftOut [string]
 *   right    node                             header right side (a range switch, a chip)
 */
export default function ExpandCard({ id, title, sub, figures = [], chart, list, detail, counted, leftOut = [], right, expanded, onToggle, color, empty = null, waiting = false }) {
  const toggleRef = useRef(onToggle); toggleRef.current = onToggle;
  const cardRef = useRef(null); const modalRef = useRef(null); const fromRef = useRef(null);
  // The dialog grows out of the card that was clicked (Lj: "with an animated opening"): the card's rect is taken at the
  // click, then the mounted dialog is animated from that rect to its own. Nothing under prefers-reduced-motion.
  const openFrom = () => { fromRef.current = cardRef.current?.getBoundingClientRect() || null; onToggle?.(true); };
  useLayoutEffect(() => {
    const m = modalRef.current; const from = fromRef.current; fromRef.current = null;
    if (!expanded || !m || !from || !m.animate || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const to = m.getBoundingClientRect();
    const dx = from.left + from.width / 2 - (to.left + to.width / 2); const dy = from.top + from.height / 2 - (to.top + to.height / 2);
    m.animate([{ transform: `translate(${dx}px, ${dy}px) scale(${Math.max(0.05, from.width / to.width)}, ${Math.max(0.05, from.height / to.height)})`, opacity: 0.35, borderRadius: '18px' }, { transform: 'none', opacity: 1 }], { duration: 280, easing: 'cubic-bezier(.2, .8, .2, 1)' });
  }, [expanded]);
  // Expanded = a full-screen modal (Lj, Oct 17: "a full screen modal or something" — the in-grid expansion read as a
  // second card and the first cut kept scrolling back to it). The page's scroll locks while it's up; Esc, the scrim or
  // the ✕ closes it. Runs once per open.
  useEffect(() => {
    if (!expanded) return undefined;
    const on = (ev) => { if (ev.key === 'Escape') toggleRef.current?.(false); };
    window.addEventListener('keydown', on);
    const prev = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', on); document.body.style.overflow = prev; };
  }, [expanded]);
  const head = (open) => (
    <div className={`${s.cardHead} ${e.head}`}>
      <h2>{title}</h2>
      {right ? <span className={e.right} onClick={(ev) => ev.stopPropagation()}>{right}</span> : null}
      {open ? <button type="button" className={e.close} onClick={() => onToggle?.(false)} aria-label={`Close ${title}`}><Icon name="x" size={16} /></button>
        : <span className={e.toggle} aria-hidden="true"><Icon name="maximize-2" size={14} /></span>}
      {sub ? <span className={s.cardSub}>{sub}</span> : null}
    </div>
  );
  const body = (open) => waiting ? <div className={e.quiet}>{empty || 'Waiting for the books’ feed to reach this device.'}</div> : (
    <>
      {figures.length ? (
        <div className={e.figs}>
          {figures.map((f, i) => (
            <div key={i} className={e.fig}>
              <b className="num" data-tone={f.tone || undefined} data-text={typeof f.value === 'string' && !/^[\d$€£−\-+.,%\s]*[\d%]/.test(f.value) ? '' : undefined} style={f.tone ? undefined : i === 0 && color ? { color } : undefined}>{f.value}</b>
              <span>{f.caption}</span>
            </div>
          ))}
        </div>
      ) : null}
      {chart ? <div className={e.chart}>{chart(open)}</div> : null}
      {list ? <div className={e.list}>{list(open)}</div> : null}
      {open && detail ? <div className={e.detail}>{detail}</div> : null}
      {counted || leftOut.length ? (
        <div className={e.counted}>
          {counted ? <span><Icon name="check-circle" size={12} />{counted}</span> : null}
          {(open ? leftOut : leftOut.slice(0, 1)).map((x, i) => <span key={i} className={e.left}><Icon name="minus-circle" size={12} />{x}</span>)}
        </div>
      ) : null}
    </>
  );
  return (
    <>
      <section id={id} ref={cardRef} className={`${s.card} ${e.card}`} style={color ? { '--cc': color } : undefined}>
        <button type="button" className={e.hit} onClick={openFrom} aria-haspopup="dialog" aria-expanded={!!expanded} aria-label={`${title} — open the details`} />
        {head(false)}
        <div className={e.body}>{body(false)}</div>
      </section>
      {expanded ? createPortal(
        <div className={e.scrim} onClick={() => onToggle?.(false)}>
          <section ref={modalRef} role="dialog" aria-modal="true" aria-label={title} className={`${s.card} ${e.card} ${e.modal}`} style={color ? { '--cc': color } : undefined} onClick={(ev) => ev.stopPropagation()}>
            {head(true)}
            <div className={e.body} data-open>{body(true)}</div>
          </section>
        </div>, document.body) : null}
    </>
  );
}

/** A small segmented switch for the card header: options [{ key, label }]. */
export function Seg({ value, options, onChange, label }) {
  return (
    <span className={e.seg} role="tablist" aria-label={label}>
      {options.map((o) => <button key={o.key} type="button" role="tab" aria-selected={value === o.key} data-on={value === o.key || undefined} onClick={() => onChange(o.key)}>{o.label}</button>)}
    </span>
  );
}

/** The by-week / by-row table under an expanded chart — the screen-reader and phone path. cols: [{ key, label, right?, format? }] */
export function DetailTable({ cols, rows, caption, onRow }) {
  if (!rows?.length) return null;
  return (
    <table className={e.table}>
      {caption ? <caption>{caption}</caption> : null}
      <thead><tr>{cols.map((c) => <th key={c.key} style={c.right === false ? { textAlign: 'left' } : undefined}>{c.label}</th>)}</tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.key || i} onClick={onRow ? () => onRow(r) : undefined} data-click={onRow ? '' : undefined}>
            {cols.map((c) => <td key={c.key} className={c.right === false ? undefined : 'num'} style={c.right === false ? { textAlign: 'left' } : undefined} data-tone={typeof c.tone === 'function' ? c.tone(r) || undefined : undefined}>{c.format ? c.format(r[c.key], r) : r[c.key] ?? '—'}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** One ranked row: label · bar (pct of max) with an optional target marker · value, a line of detail under. */
export function RankRow({ label, sub, value, pct, markerPct, color, tone, onClick, right }) {
  const inner = (
    <>
      <div className={e.rTop}><span className={e.ell}>{label}</span><b className="num" data-tone={tone || undefined}>{value}</b></div>
      <div className={e.rTrack}>
        <span className={e.rFill} style={{ width: `${Math.max(0, Math.min(100, pct || 0))}%`, background: color || 'var(--cc, var(--c-accent))' }} />
        {markerPct != null ? <i className={e.rMark} style={{ left: `${Math.max(0, Math.min(100, markerPct))}%` }} /> : null}
      </div>
      {(sub || right) ? <div className={e.rSub}><span className={e.ell}>{sub}</span>{right ? <span>{right}</span> : null}</div> : null}
    </>
  );
  return onClick ? <button type="button" className={e.row} onClick={(ev) => { ev.stopPropagation(); onClick(); }}>{inner}</button> : <div className={e.row}>{inner}</div>;
}
